import { createSelector } from 'reselect';
import { filter, flatMap, keyBy, map, sortBy } from 'es-toolkit/compat';

import { StoreType } from '@tock/shared/utils/connect';
import { core } from '@tock/coretypes';
import { hasMinGlobalRoleHospitalitySelector } from 'selectors/rolesSelectors';
import {
  isDiningVariety,
  isEventVariety,
  isTicketTypeDeliveryVariety,
  isTicketTypePickupVariety,
  isTicketTypeTablesideVariety,
} from '@tock/shared/utils/offerings';
import { sortComputedEventTickets } from '@tock/shared/utils/eventUtils';

export const availabilityPlanningActiveExperiencesSelector = createSelector(
  (state: StoreType) => state.templates.tickets,
  (ticketMap) =>
    Object.values(ticketMap ?? {}).filter(
      (experience) =>
        // same as templateInfo.allActiveTickets
        !experience.isArchived && experience.isBookable && isDiningVariety(experience.variety)
    )
);

export const ticketTypeSelector = createSelector(
  (state: StoreType) => state.tickets,
  (tickets) => tickets.single
);

export const ticketsSelector = (state: StoreType) => state.tickets;

export const ticketTypesListStateSelector = (state: StoreType) => state.tickets.list;

export const ticketTypesListSelector = createSelector(
  ticketsSelector,
  (tickets: StoreType['tickets']) => {
    return tickets.list.ticketTypes;
  }
);

export const eventTemplatesSelector = createSelector(
  ticketsSelector,
  (tickets: StoreType['tickets']) => {
    return tickets.single.eventTemplates;
  }
);

export const eventTemplatesListSelector = createSelector(
  ticketsSelector,
  (tickets: StoreType['tickets']) => {
    return tickets.list.eventTemplates;
  }
);

// exported for testing
export function computeSortedEvents(
  ticketTypes: Array<core.TicketType>,
  eventTemplates: {
    [key: number]: core.EventTemplate[];
  }
): {
  ticketType: core.TicketType;
  eventTemplates: core.EventTemplate[];
}[] {
  const sortedOfferings = sortComputedEventTickets(ticketTypes, eventTemplates);
  const offerings = map(sortedOfferings, (ticketType: core.TicketType) => {
    return { ticketType, eventTemplates: eventTemplates[ticketType.id] ?? [] };
  });
  return offerings;
}

export const sortedEventsSelector = createSelector(
  ticketTypesListSelector,
  eventTemplatesListSelector,
  computeSortedEvents
);

export const ticketTypeFromListSelector = (ticketTypeId: number) =>
  createSelector(ticketTypesListSelector, (ticketTypes: core.TicketType[]) => {
    return ticketTypes.find((ticketType) => ticketType.id === ticketTypeId);
  });

export const toGoTicketTypesSelector = createSelector(ticketTypesListSelector, (ticketTypes) =>
  ticketTypes.filter(
    (ticketType) => isTicketTypeDeliveryVariety(ticketType) || isTicketTypePickupVariety(ticketType)
  )
);

export const bookableTicketTypesSelector = createSelector(
  ticketTypesListSelector,
  hasMinGlobalRoleHospitalitySelector,
  (ticketTypes, isEngOrHospitality) => {
    const bookableTicketTypes = filter(
      ticketTypes,
      (ticketType) => ticketType.isBookable || isEngOrHospitality
    );
    return sortBy(bookableTicketTypes, 'ordering');
  }
);

export const bookableTicketTypesByIdSelector = createSelector(
  bookableTicketTypesSelector,
  (ticketTypes: core.TicketType[]): Dictionary<core.TicketType> => keyBy(ticketTypes, 'id')
);

export const activeTicketsSelector = createSelector(bookableTicketTypesSelector, (ticketTypes) =>
  ticketTypes.filter((ticketType) => !ticketType.isArchived)
);

export const activeInventoryEligibleTicketsSelector = createSelector(
  activeTicketsSelector,
  (activeTickets) => activeTickets.filter((ticketType) => !isTicketTypeTablesideVariety(ticketType))
);

export const activeTicketsByIdSelector = createSelector(
  activeTicketsSelector,
  (ticketTypes: core.TicketType[]): Dictionary<core.TicketType> => {
    return keyBy(ticketTypes, 'id');
  }
);

export const archivedTicketsSelector = createSelector(bookableTicketTypesSelector, (ticketTypes) =>
  ticketTypes.filter((ticketType) => ticketType.isArchived)
);

export const unarchivedTicketsSelector = createSelector(
  (state: StoreType) => state.tickets.list.ticketTypes,
  (data) => {
    return filter(data, (ticket) => !ticket.isArchived);
  }
);

export const bookableDiningTicketTypes = createSelector(activeTicketsSelector, (ticketTypes) =>
  ticketTypes.filter(({ variety, isBookable }) => isDiningVariety(variety) && isBookable)
);

export function computeOptionGroups(ticketTypes: core.TicketType[]): core.TicketOptionGroup[] {
  return flatMap(ticketTypes, (ticket) => ticket.optionGroup as core.TicketOptionGroup[]);
}

export function computeOptions(optionGroups: core.TicketOptionGroup[]): core.TicketOption[] {
  return flatMap(optionGroups, (group) => group.ticketOption as core.TicketOption[]);
}

export const optionGroupsSelector = (ticketTypeId?: number) => {
  return createSelector(ticketTypesListSelector, (ticketTypes: core.TicketType[]) =>
    computeOptionGroups(
      ticketTypes.filter(
        ticketTypeId ? (ticketType: core.TicketType) => ticketType.id === ticketTypeId : () => true
      )
    )
  );
};

export const optionsSelector = (ticketTypeId?: number) => {
  return createSelector(optionGroupsSelector(ticketTypeId), computeOptions);
};

/* istanbul ignore next */
export const eventExperienceIdsSelector = createSelector(ticketTypesListSelector, (experiences) =>
  experiences.reduce<number[]>((arr, experience) => {
    if (isEventVariety(experience.variety)) {
      arr.push(experience.id);
    }
    return arr;
  }, [])
);
